/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 그리드의 텍스트 필터 클래스를 정의한다.
 *
 * @file /src/models/table/filters/Text.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 16.
 */
import * as parent from './Filter';
import { Text as TextField } from '@components/form/field/text/Text';

export interface Properties extends parent.Properties {
    /**
     * 필터타입
     */
    type: 'text';

    /**
     * 검색방식
     */
    operator?: 'like' | 'likecode' | '=';
}

export class Text extends parent.Filter {
    /**
     * 필터타입
     */
    type: string = 'text';

    /**
     * 검색방식
     */
    operator: 'like' | 'likecode' | '=';

    /**
     * 텍스트 필터를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        super(properties);

        this.operator = this.properties.operator ?? 'like';
    }

    /**
     * 필터필드를 가져온다.
     *
     * @returns 필드
     */
    getField(): TextField {
        if (this.field === null) {
            this.field = new TextField({
                width: 200,
                listeners: {
                    keyboard: async (e) => {
                        if (e.key == 'Enter') {
                            this.getButton().onClick();
                        }
                    },
                },
            });
        }

        return this.field as TextField;
    }

    /**
     * 필터객체를 가져온다.
     *
     * @returns 필터객체
     */
    async getFilter(): Promise<{ value: any; operator: string } | null> {
        const value = (await this.getField().getValue()) ?? '';
        if (value.length == 0) {
            return null;
        }

        return { value: value, operator: this.operator };
    }

    /**
     * 현재 필터설정을 필터필드에 적용한다.
     *
     * @param filter - 필터객체
     */
    async setFilter(filter: { value: any; operator: string } | null): Promise<void> {
        this.getButton().setDisabled(true);
        if (filter === null) {
            await this.getField().setValue('');
        } else if (typeof filter.value == 'string' || typeof filter.value == 'number') {
            await this.getField().setValue(filter.value.toString());
            if (['like', 'likecode', '='].includes(filter.operator) == true) {
                this.operator = filter.operator as 'like' | 'likecode' | '=';
            }
        }
        this.getButton().setDisabled(false);
    }
}
